export type Feature =
  | "courses"
  // Admin-only course authoring. Separate from "courses" so a tenant can
  // build courses in /admin before they are exposed to users.
  | "adminCourses"
  | "library"
  | "consultations"
  | "report"
  | "potty"
  | "sleep"
  | "admin"
  | "player";

export interface TenantConfig {
  id: "kidology" | "mindfulme";
  brandName: string;
  features: ReadonlySet<Feature>;
  hasCourses: boolean;
  hasConsultations: boolean;
  hasReport: boolean;
  homePath: string;
}

function build(
  id: TenantConfig["id"],
  brandName: string,
  features: Feature[],
): TenantConfig {
  const set = new Set<Feature>(features);
  return {
    id,
    brandName,
    features: set,
    hasCourses: set.has("courses"),
    hasConsultations: set.has("consultations"),
    hasReport: set.has("report"),
    homePath: set.has("courses") && !set.has("report") ? "/courses" : "/report",
  };
}

// Single source of truth: a feature missing here is not routed, not linked
// and its lazy chunk is never fetched on that tenant's domain.
const TENANTS = {
  kidology: build("kidology", "Kidology", ["courses", "adminCourses", "library", "consultations", "admin", "player"]),
  mindfulme: build("mindfulme", "MindfulMe", ["report", "courses", "consultations", "potty", "sleep", "admin", "adminCourses", "player"]),
} as const;

function resolveTenant(): TenantConfig {
  if (typeof window === "undefined") return TENANTS.kidology;
  const hostname = window.location.hostname.toLowerCase();
  return hostname.includes("mindfulme") ? TENANTS.mindfulme : TENANTS.kidology;
}

let cached: TenantConfig | null = null;

export function getTenantConfig(): TenantConfig {
  if (cached) return cached;
  const tenant = resolveTenant();
  if (typeof window !== "undefined") {
    const root = document.documentElement;
    if (tenant.id === "mindfulme") {
      root.setAttribute("data-tenant", "mindfulme");
      root.classList.add("tenant-mindfulme");
    } else {
      root.removeAttribute("data-tenant");
      root.classList.remove("tenant-mindfulme");
    }
  }
  cached = tenant;
  return tenant;
}

export function hasFeature(feature: Feature): boolean {
  return getTenantConfig().features.has(feature);
}
